import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../../lib/brand";

const SUGGESTIONS = ["Abundance", "Clarity", "Peace", "Courage", "Gratitude"];

let RID = 0;

export const Intention = () => {
  const [value, setValue] = useState("");
  const [charged, setCharged] = useState(null);
  const [ripples, setRipples] = useState([]);
  const [floaters, setFloaters] = useState([]);
  const inputRef = useRef(null);

  const charge = (raw) => {
    const word = (raw || "").trim();
    if (!word) {
      toast.error("Speak your intention into the water first.");
      inputRef.current?.focus();
      return;
    }
    setCharged(word);
    const id = ++RID;
    setRipples((r) => [...r, id]);
    setTimeout(() => setRipples((r) => r.filter((x) => x !== id)), 2200);

    setFloaters((f) =>
      [
        ...f,
        {
          id,
          text: word,
          left: 12 + Math.random() * 70,
          top: 18 + Math.random() * 60,
          size: 0.9 + Math.random() * 0.9,
        },
      ].slice(-8),
    );
    toast.success("Your intention is now charged into the water.", {
      description: `“${word}” will travel with every sip.`,
    });
    setValue("");
  };

  return (
    <section
      id="intention"
      data-testid="intention-section"
      className="relative min-h-screen overflow-hidden bg-[#020505]"
    >
      {/* Water backdrop */}
      <img
        src={IMAGES.waterReflection}
        alt="Rippling water"
        className="absolute inset-0 h-full w-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#020505] via-[#02050599] to-[#020505]" />
      <div className="absolute inset-0 bg-black/40" />

      {/* Floating charged words */}
      <AnimatePresence>
        {floaters.map((f) => (
          <motion.span
            key={f.id}
            initial={{ opacity: 0, scale: 0.6, filter: "blur(8px)" }}
            animate={{ opacity: 0.5, scale: 1, filter: "blur(0px)", y: [0, -12, 0] }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.2 },
              y: { duration: 8, repeat: Infinity, ease: "easeInOut" },
            }}
            style={{ left: `${f.left}%`, top: `${f.top}%`, fontSize: `${f.size}rem` }}
            className="pointer-events-none absolute font-serif italic text-teal-200/60 select-none"
          >
            {f.text}
          </motion.span>
        ))}
      </AnimatePresence>

      <div className="relative z-10 mx-auto max-w-3xl px-6 min-h-screen flex flex-col items-center justify-center text-center">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center gap-2 font-mono text-[11px] tracking-[0.4em] text-teal-300"
        >
          <Sparkles size={14} /> THE RITUAL
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-6 font-serif font-light tracking-tighter text-5xl md:text-7xl text-zinc-50 leading-[1]"
        >
          Set Your <span className="italic text-teal-300">Intention</span>
        </motion.h2>

        <p className="mt-6 max-w-lg text-zinc-300 font-light">
          Speak a word into the water. Watch it settle in. Every bottle you
          drink carries the intention you charge it with.
        </p>

        {/* Charged word display + ripples */}
        <div className="relative mt-12 mb-4 h-28 w-full flex items-center justify-center">
          {ripples.map((id) => (
            <span
              key={id}
              className="absolute h-24 w-24 rounded-full border border-teal-300/50"
              style={{ animation: "ripple 2.1s ease-out forwards" }}
            />
          ))}
          <AnimatePresence mode="wait">
            {charged && (
              <motion.span
                key={charged + RID}
                initial={{ opacity: 0, scale: 0.7, filter: "blur(6px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                data-testid="charged-word"
                className="font-serif italic text-4xl md:text-6xl text-teal-200 text-glow"
              >
                {charged}
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {/* Input */}
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-3 border-b border-white/20 focus-within:border-teal-300 transition-colors pb-3">
            <input
              ref={inputRef}
              data-testid="intention-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && charge(value)}
              maxLength={24}
              placeholder="Type your intention…"
              className="flex-1 bg-transparent text-center text-xl md:text-2xl font-serif italic text-zinc-100 placeholder:text-zinc-500 outline-none"
            />
            <button
              data-testid="intention-submit"
              onClick={() => charge(value)}
              className="grid place-items-center h-11 w-11 rounded-full bg-teal-400 text-black transition-all hover:bg-teal-300 hover:scale-105"
              aria-label="Charge intention"
            >
              <ArrowRight size={18} />
            </button>
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid={`suggestion-${s.toLowerCase()}`}
                onClick={() => charge(s)}
                className="font-mono text-[11px] tracking-[0.15em] rounded-full border border-white/15 px-4 py-2 text-zinc-300 hover:border-teal-300 hover:text-teal-200 transition-all duration-300"
              >
                {s.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Intention;
